import { useState } from 'react';
import { Icon } from './Icon.jsx';
import { color, font, radius } from '../theme/tokens.js';

/**
 * A restaurant card, in the one shape the product uses everywhere.
 *
 * Full-bleed photograph, a scrim so type stays readable over any image, the
 * name over the foot of it and the rating in a pill top-right. That is what
 * `mobile/src/features/discover/SwipeCard.tsx` renders, and this is the web's
 * single copy of it.
 *
 * It lives in `components/` rather than beside either caller because it has two
 * of them: the landing page's demo reel, and the restaurant dashboard's card
 * preview. A restaurant designing a promoted card has to be looking at the
 * thing diners will actually see — if the preview drifts from the real card,
 * the preview is worse than useless, because it is confidently wrong.
 *
 * Purely presentational, and takes a plain photo URL rather than anything
 * Places-shaped: the landing page passes a proxied Places photo, the dashboard
 * passes whatever the restaurant uploaded, and neither concern belongs here.
 *
 * @param {object} props
 * @param {string|null} [props.photoUrl]
 * @param {string} props.name
 * @param {string} [props.meta]  The line under the name — cuisine, area, price.
 * @param {number|null} [props.rating]  Hidden when absent rather than faked.
 * @param {number} [props.infoOpacity]  Fades the caption; the deck hides it on
 *   cards behind the front one, whose foot is the part that peeks out.
 * @param {React.ReactNode} [props.children]  Overlays — vote stamps, a badge.
 */
export function RestaurantCard({ photoUrl, name, meta, rating = null, infoOpacity = 1, children }) {
  const [artFailed, setArtFailed] = useState(false);
  const caption = { opacity: infoOpacity, transition: 'opacity 0.28s ease' };

  return (
    <div style={styles.card}>
      {photoUrl && !artFailed && (
        <img
          src={photoUrl}
          alt=""
          draggable="false"
          onError={() => setArtFailed(true)}
          style={styles.photo}
        />
      )}
      <div style={styles.scrim} />

      {children}

      {rating != null && (
        <div style={{ ...styles.pill, ...caption }}>
          <Icon name="star" size={11} stroke={color.star} fill={color.star} strokeWidth={0} />
          {rating.toFixed(1)}
        </div>
      )}

      <div style={{ ...styles.info, ...caption }}>
        <p style={styles.name}>{name}</p>
        {meta && <p style={styles.meta}>{meta}</p>}
      </div>
    </div>
  );
}

const styles = {
  card: {
    position: 'relative',
    height: '100%',
    borderRadius: radius.card,
    overflow: 'hidden',
    // Shows through until the photograph loads, and stays if it never does.
    background: color.onDark,
    boxShadow: '0 14px 34px rgba(10,16,4,0.30)',
  },

  photo: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },

  scrim: {
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.80) 100%)',
  },

  pill: {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 10px',
    borderRadius: radius.pill,
    background: 'rgba(255,255,255,0.94)',
    color: color.ink,
    fontSize: 12,
    fontWeight: font.weight.bold,
  },

  info: { position: 'absolute', left: 16, right: 16, bottom: 14 },

  name: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.12,
    fontWeight: font.weight.heavy,
    letterSpacing: '-0.5px',
    color: color.inkInverse,
  },

  meta: {
    margin: '4px 0 0',
    fontSize: 12.5,
    fontWeight: font.weight.medium,
    color: 'rgba(255,255,255,0.86)',
  },
};
